<script setup lang="ts">
import {
  useToast,
  useImage,
  AtomIcon,
  AtomText,
  AtomButton,
  SatIconButton,
  SatTooltip,
  AtomBadge,
  useDate,
} from "@chatfood/design-system";
import { bemBuilder, toMoney } from "@chatfood/core-utils";
import { ref, computed } from "vue";
import { FetchError } from "ofetch";
import { useLocale } from "~/composables/use-locale";
import type { IDomainLoyaltyProgram } from "~/domain/loyalty-program";
import { useBasketStore } from "~/stores/basket";

const css = bemBuilder("loyalty-detail");

const props = defineProps<{ loyalty: IDomainLoyaltyProgram }>();
const emits = defineEmits<{ back: [MouseEvent]; close: [MouseEvent] }>();

const { t } = useLocale();
const { optimize } = useImage();
const { getReadableDate } = useDate();
const { toastError, toastSuccess } = useToast();

const basket = useBasketStore();

const isApplicable = computed(() => props.loyalty.isApplicable);
const alreadyApplied = computed(() =>
  basket.loyalties.find((loyalty) => loyalty.id === props.loyalty.id),
);
const anotherLoyaltyApplied = computed(
  () => basket.loyalties.length && !alreadyApplied.value,
);
const discountApplied = computed(() => basket.discount);

const canApply = computed(() => {
  return (
    isApplicable.value &&
    !alreadyApplied.value &&
    !anotherLoyaltyApplied.value &&
    !discountApplied.value
  );
});

const tooltipApply = computed(() => {
  if (!isApplicable.value)
    return t("components.loyalty_detail.tooltip_cta_is_not_applicable");
  if (alreadyApplied.value)
    return t("components.loyalty_detail.tooltip_cta_loyalty_applied");
  if (anotherLoyaltyApplied.value)
    return t("components.loyalty_detail.tooltip_cta_another_loyalty_applied");
  if (discountApplied.value)
    return t("components.loyalty_detail.tooltip_cta_has_discount");

  return "";
});

const isApplying = ref(false);

const isLoyaltyApplied = () => {
  if (basket.loyalties.find((loyalty) => loyalty.id === props.loyalty.id)) {
    toastSuccess(t("components.loyalty_detail.apply_message_success"));
  } else {
    toastError(t("components.loyalty_detail.toast_apply_error_not_eligible"));
  }
};

const onApply = async (event: MouseEvent) => {
  if (!canApply.value) return;

  isApplying.value = true;

  try {
    await basket.applyLoyalty(props.loyalty.id);

    isLoyaltyApplied();

    emits("close", event);
  } catch (error: unknown) {
    const applyErrorMessages: Record<string, string> = {
      loyalty_already_applied: t(
        "components.loyalty_detail.toast_apply_error_already_applied",
      ),
      loyalty_not_eligible: t(
        "components.loyalty_detail.toast_apply_error_not_eligible",
      ),
    };

    let errorMessage = t("components.loyalty_detail.toast_apply_error_generic");

    if (
      error instanceof FetchError &&
      error.statusCode === 422 &&
      error.data.statusMessage
    ) {
      const statusMessage = applyErrorMessages[error.data.statusMessage];
      errorMessage = statusMessage ?? errorMessage;
    }

    toastError(errorMessage);
  } finally {
    isApplying.value = false;
  }
};

function getImage(photo?: string | null) {
  if (!photo) return "";
  return `background-image: url(${optimize(photo, { w: 600, h: 600, fit: "crop" })})`;
}
</script>

<template>
  <div :class="css('')">
    <div :class="css('header')">
      <SatIconButton
        theme="ghost"
        icon-name="back"
        @click="emits('back', $event)"
      />
    </div>
    <div :class="css('content')">
      <div
        data-test="image"
        :class="css('image')"
        :style="getImage(props.loyalty.photo)"
      >
        <AtomIcon
          v-if="!props.loyalty?.photo"
          size="2x-large"
          color="color-base-70"
          name="placeholder-photo"
          data-test="placeholder-image"
        />
      </div>
      <AtomText type="h3" color="color-text-headline">
        {{ props.loyalty.title }}
      </AtomText>
      <AtomText v-if="props.loyalty?.description" color="color-text-secondary">
        {{ props.loyalty.description }}
      </AtomText>
      <div>
        <AtomBadge
          v-if="
            props.loyalty.type === 'discount_percentage' ||
            props.loyalty.type === 'item_discount_percentage'
          "
          :value="props.loyalty.discountPercentage"
          size="normal"
          emphasis="high"
          state="positive"
        />
        <AtomBadge
          v-if="props.loyalty.discount"
          :value="
            toMoney(
              props.loyalty.discount.value,
              props.loyalty.discount.currency,
            )
          "
          size="normal"
          emphasis="high"
          state="positive"
        />
      </div>
    </div>
    <div :class="css('footer')">
      <AtomText
        v-if="props.loyalty?.expiresAt"
        type="small-bold"
        data-test="expires-at"
        color="color-text-body"
      >
        {{
          t("components.loyalty_detail.available_until_x", {
            expiresAt: getReadableDate(props.loyalty.expiresAt, "DD/MM/YYYY"),
          })
        }}
      </AtomText>
      <SatTooltip :content="tooltipApply">
        <AtomButton
          wide
          size="large"
          theme="filled"
          data-test="cta"
          variant="primary"
          :class="css('cta')"
          :is-loading="isApplying"
          :is-disabled="!canApply"
          @click.prevent="onApply($event)"
        >
          <span :class="css('cta-label')">
            {{ t("components.loyalty_detail.apply_cta") }}
          </span>
          <template
            v-if="
              typeof props.loyalty.cost === 'number' && props.loyalty.cost > 0
            "
          >
            <span :class="css('divider')"></span>
            <span :class="css('cost')" data-test="cost">
              <AtomIcon size="small" name="rating" ignore-color />
              {{ props.loyalty.cost }}
            </span>
          </template>
        </AtomButton>
      </SatTooltip>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.loyalty-detail {
  &__image {
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radii-card-md);
    background-size: cover;
    background-position: center;
    background-color: var(--color-base-95);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-md);
  }

  &__cta {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);

    &-label {
      flex: 1;
    }
  }

  &__cost {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
  }

  &__divider {
    display: inline-block;
    width: 1px;
    opacity: 0.2;
    align-self: stretch;
    margin: 4px 0;
    background-color: var(--color-base-100);
  }

  &__content,
  &__footer {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    padding: var(--spacing-md);
  }

  &__footer {
    text-align: center;
    gap: var(--spacing-sm);
  }
}
</style>
